import type { AiCapabilityPage, AiPageGroupRole, AiSlot } from "../schema.js";

/**
 * Mảnh dùng chung cho các trang của Sổ (US-AIV2-0013). CHỈ hằng và kiểu — không luật, không hàm tính.
 * Luật của một trang nằm ở chính trang; luật của Sổ nằm ở `validate.ts`. File này tồn tại để các trang
 * không chép tay cùng một mảng vai / cùng một ô "quỹ nào" N lần rồi trôi khỏi nhau (số trang: đếm
 * `AI_CAPABILITY_REGISTRY`, đừng chép).
 */

/** Vai quản trị — ba vai được ghi sổ thay nhóm (khớp `PAYMENT_APPROVER_ROLES` của contracts). */
export const MANAGER_ROLES: readonly AiPageGroupRole[] = ["owner", "admin", "treasurer"];
/** Mọi vai trong nhóm — trang đọc và trang tự-khai của chính mình. */
export const ALL_ROLES: readonly AiPageGroupRole[] = ["owner", "admin", "treasurer", "member"];
/** Chủ nhóm + quản trị — việc cấu trúc nhóm (thêm người, tạo quỹ). */
export const OWNER_ADMIN: readonly AiPageGroupRole[] = ["owner", "admin"];
/** Chỉ chủ nhóm — việc GUI gác `@RequireGroupRoles(OWNER)` (chia sẻ sổ minh bạch). */
export const OWNER_ONLY: readonly AiPageGroupRole[] = ["owner"];

/**
 * Cờ tính năng cho MỌI trang ghi (T1/T2) ở MVP — cùng cờ v1 `ai_chat_t1_write` (vắng row vẫn OFF, CLAUDE.md §4).
 * 0023 đọc cờ ở CẢ start() lẫn confirm(). Tách cờ T2 riêng là QĐ của 0023/PO, không phải của trang.
 */
export const WRITE_FLAGS: readonly string[] = ["ai_chat_t1_write"];

/** Ô "quỹ nào" — tuỳ chọn khi người dùng chỉ có một quỹ (resolver 0022 tự điền). */
export const SLOT_FUND: AiSlot = {
  id: "quy",
  type: "fund",
  required: false,
  ask: "Quỹ nào? Bạn đang có nhiều quỹ trong nhóm.",
  // 0023b (`@UC0226`): 1 quỹ đang hoạt động ⇒ tự điền, phiếu vẫn in tên; ≥2 ⇒ hỏi; 0 ⇒ dẫn sang tạo quỹ.
  // `emptyAsk` là câu TRẦN THUẬT (review #825 M3): trang mở cho mọi vai (READ-FUND-MONEY) mà người hỏi không tạo được quỹ ⇒ menu 0 nút; một câu
  // mời "tạo quỹ trước nhé?" không kèm nút là ngõ cụt. Nút `run_page createPage` chỉ hiện khi người đó được phép — lời mời nằm ở nút.
  autofill: "sole-active",
  createPage: "ONB-CREATE-FUND",
  emptyAsk: "Nhóm chưa có quỹ nào đang hoạt động.",
};
/** Ô "nhóm nào" — tuỳ chọn khi phiên đã có nhóm hiệu lực. */
export const SLOT_GROUP: AiSlot = {
  id: "nhom",
  type: "group",
  required: false,
  ask: "Ở nhóm nào?",
  autofill: "sole-active",
  createPage: "ONB-CREATE-GROUP",
  emptyAsk: "Bạn chưa ở trong nhóm nào — tạo nhóm trước nhé?",
};
/**
 * Ô "đợt thu nào" của trang GHI trên một khoản (0023b · AC4: báo · rút báo · ghi nhận · nhắc): đợt ĐANG MỞ trong quỹ đã chốt của phiếu; 1 ⇒ tự điền;
 * 0 ⇒ dẫn sang mở đợt thu. KHÔNG dùng cho trang đọc có ngữ nghĩa "bỏ trống = đợt gần nhất" (READ-COLLECTIONS — review #825 M3). `emptyAsk` trần
 * thuật, không nói "quỹ này" (trang có thể không có ô quỹ), lời mời nằm ở nút.
 */
export const SLOT_COLLECTION_AUTOFILL: Pick<AiSlot, "type" | "autofill" | "createPage" | "emptyAsk"> = {
  type: "collection",
  autofill: "sole-active",
  createPage: "COL-EQUAL",
  emptyAsk: "Chưa có đợt thu nào đang mở.",
};

export const DRAFT: AiCapabilityPage["status"] = "draft";
/** Trang đã có binding executor thật (0031+) — chạy được ở dev/staging; production đòi `live` (QĐ-2 0023). */
export const BOUND: AiCapabilityPage["status"] = "bound";
/**
 * 🔴 US-AIV2-0099 (@UC0159 · QĐ PO 23/09) — trang ĐÃ QUA eval. Khác `BOUND` ở một điểm quyết định:
 * `evaluated` nằm trong `AI_EVAL_GATED_STATUSES` ⇒ **cổng eval GÁC trang này**, tụt dưới ngưỡng là
 * cổng ĐỎ. Chỉ nâng khi bảng điểm cho `pass: true` + đạt cả ba ngưỡng trên mẫu số ĐẦY ĐỦ.
 */
export const EVALUATED: AiCapabilityPage["status"] = "evaluated";
/**
 * 🔴 US-AIV2-0110 S04a (@UC0277 · QĐ PO 27/09) — trang CHẠY ĐƯỢC Ở PRODUCTION: `ai-capability-gate.service.ts` `isRunnable`
 * cho production chỉ nhận `live`. QĐ PO 27/09 bật `live` cho các trang đã eval xanh (số hiện hành: đọc `AI_CAPABILITY_REGISTRY`)
 * — **SAU** khi US-AIV2-0065 xong (G14h đỏ khi Sổ có
 * trang `live` mà gate v2 chưa viết; đó là chốt CỐ Ý). 🔴 QĐ người dùng 03/11/2026 (`@UC0159`, AI-SMOOTH-1): nâng lên `live` KHÔNG còn
 * đòi eval xanh — đòi binding có test (lưới `executor-binding.invariants.spec.ts`) + người nghiệm thu bằng bộ câu thật (ghi ở PR).
 */
export const LIVE: AiCapabilityPage["status"] = "live";
